import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import { useQueryClient, useMutation } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import styles from './responseReviewerConversation.module.scss';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import TextArea from 'v2/form/TextArea/TextArea';
import { SearchEntity } from 'v2/models';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';

import { Button } from 'react-bootstrap';
import commentDots from '../../icons/commentDots.svg';
import { FEATURE_FLAGS } from 'utils/constants';
import { useFeatureFlagContext } from 'v2/context/featureFlagContext';
import { getKey as getSearchConversationCountKey } from 'v2/queries/useSearchConversationCount';
import { getKey as getSearchDetailsKey } from 'v2/queries/searches/useSearchDetails';

interface ReviewerResponseData {
  respondMessage: string;
}

interface ResponseReviewerProps {
  search: SearchEntity;
  disabled?: boolean;
  onChange?: () => void;
  onError?: (error: string) => void;
  onComplete?: () => void;
}

const ResponseReviewerConversation = ({
  search,
  onComplete,
  disabled,
  onChange,
  onError,
}: ResponseReviewerProps) => {
  const [showResponseModal, setShowResponseModal] = useState(false);
  const { flags } = useFeatureFlagContext();
  const isAutoAssignEnabled = flags && flags[FEATURE_FLAGS.REVIEWER_ASSIGNMENT];
  const { handleSubmit, register } = useForm<ReviewerResponseData>({
    mode: 'onChange',
  });

  const queryClient = useQueryClient();

  const { mutate: createConversation, isLoading: isCreateConversationLoading } =
    useMutation(
      async (data: ReviewerResponseData) => {
        if (isAutoAssignEnabled) {
          await restFactory.get(
            `/dj/api/v1/search/${search.id}/assign_to_reviewer/`,
            { action: 'assign' }
          );
        }
        return restFactory.post('/conversations/create_reviewer_conversation', {
          search: search.id,
          message: data.respondMessage,
        });
      },
      {
        onSuccess: async () => {
          onChange?.();
          queryClient.invalidateQueries(
            getSearchConversationCountKey(search.id)
          );
          toast.success('Response sent');
        },
        onError: (error: AxiosError | string) => {
          const errorMessage =
            typeof error === 'string'
              ? error
              : 'Internal Error Occurred. Please try again later.';
          toast.error(errorMessage);
          onError?.(errorMessage);
        },
        onSettled: () => {
          onComplete?.();
          queryClient.invalidateQueries(getSearchDetailsKey(search.id));
          closeModalSend();
        },
      }
    );

  const responseModalSend = async (values: ReviewerResponseData) => {
    createConversation(values);
  };

  const closeModalSend = () => {
    setShowResponseModal(false);
  };

  const handleRespondToOriginatorClick = () => {
    setShowResponseModal(true);
  };

  return (
    <>
      {search.reviewer_response ? (
        <Link
          className={classnames(
            'mr-2',
            'btn-secondary-dark',
            'btn btn-secondary',
            { disabled: isCreateConversationLoading },
            styles.conversationButton,
            styles.conversationLink
          )}
          to={`/reviewer/request-details/${search.id}/conversations`}
        >
          <img src={commentDots} alt={'commentDots'} className={'pr-2'}></img>
          View conversation
        </Link>
      ) : (
        <Button
          onClick={handleRespondToOriginatorClick}
          className={classnames(
            'mr-2',
            'btn btn-secondary-dark',
            'btn btn-secondary',
            styles.conversationButton
          )}
          disabled={disabled || isCreateConversationLoading}
        >
          <img src={commentDots} alt={'commentDots'} className={'pr-2'}></img>
          Respond to Originator
        </Button>
      )}

      <ConfirmationModal
        centered={true}
        animation={true}
        show={showResponseModal}
        onCancel={() => closeModalSend()}
        okBtnText="Send"
        onOk={handleSubmit(responseModalSend)}
        title="Send a Message to the Originator"
        isLoading={isCreateConversationLoading}
      >
        <>
          <span className="text-light3">
            Please provide your note to the originator below:
          </span>
          <TextArea
            aria-label="Response Reviewer Message Box"
            label=""
            className={classnames('mt-3', styles.responseText)}
            register={register('respondMessage', { required: true })}
            autoComplete="off"
            required={true}
          />
        </>
      </ConfirmationModal>
    </>
  );
};

export default ResponseReviewerConversation;
